import { useState, useCallback } from 'react'
import { Camera } from '@capacitor/camera'
import { Capacitor } from '@capacitor/core'

interface CameraPermissionsState {
  isLoading: boolean
  hasPermission: boolean | null
  error: string | null
}

interface UseCameraPermissionsReturn {
  permissionState: CameraPermissionsState
  requestPermissions: () => Promise<boolean>
  checkPermissions: () => Promise<boolean>
  resetError: () => void
}

export const useCameraPermissions = (): UseCameraPermissionsReturn => {
  const [permissionState, setPermissionState] = useState<CameraPermissionsState>({
    isLoading: false,
    hasPermission: null,
    error: null,
  })

  const resetError = useCallback(() => {
    setPermissionState((prev) => ({ ...prev, error: null }))
  }, [])

  const checkPermissions = useCallback(async (): Promise<boolean> => {
    if (!Capacitor.isNativePlatform()) {
      setPermissionState((prev) => ({ ...prev, hasPermission: true }))
      return true
    }

    try {
      setPermissionState((prev) => ({ ...prev, isLoading: true, error: null }))

      const permissions = await Camera.checkPermissions()
      const hasPermission = permissions.camera === 'granted'

      setPermissionState((prev) => ({
        ...prev,
        hasPermission,
        isLoading: false,
      }))

      return hasPermission
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Error while checking permissions'

      setPermissionState((prev) => ({
        ...prev,
        hasPermission: false,
        isLoading: false,
        error: errorMessage,
      }))

      return false
    }
  }, [])

  const requestPermissions = useCallback(async (): Promise<boolean> => {
    if (!Capacitor.isNativePlatform()) {
      setPermissionState((prev) => ({ ...prev, hasPermission: true }))
      return true
    }

    try {
      setPermissionState((prev) => ({ ...prev, isLoading: true, error: null }))

      const permissions = await Camera.requestPermissions()
      const hasPermission = permissions.camera === 'granted'

      setPermissionState((prev) => ({
        ...prev,
        hasPermission,
        isLoading: false,
      }))

      return hasPermission
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Error while requesting permissions'

      setPermissionState((prev) => ({
        ...prev,
        hasPermission: false,
        isLoading: false,
        error: errorMessage,
      }))

      return false
    }
  }, [])

  return {
    permissionState,
    requestPermissions,
    checkPermissions,
    resetError,
  }
}
